import { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { createCheckout, markTransactionCacheStale } from '../../services/paymentService';
import { buildCheckoutParams, stashPendingCheckoutUuid } from '../../utils/paymentCheckout';
import Button from '../common/Button';

/**
 * Payment button that starts a provider-neutral hosted checkout. The backend
 * decides per site whether Yoco or Peach handles it; the flow is identical.
 *
 * @param {Object} props
 * @param {number} props.amount - Amount in integer cents
 * @param {string} props.accountUuid - Account UUID
 * @param {string} props.unitUuid - Unit UUID
 * @param {Function} props.onCheckoutCreated - Callback when checkout is created
 * @param {Function} props.onError - Callback for errors
 * @param {boolean} props.disabled - Whether the button is disabled externally
 */
const PaymentButton = ({
  amount,
  accountUuid,
  unitUuid,
  onCheckoutCreated = () => {},
  onError = () => {},
  disabled = false
}) => {
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();

  const handlePaymentClick = async () => {
    if (disabled || loading) {
      return;
    }

    try {
      setLoading(true);
      markTransactionCacheStale();

      const params = buildCheckoutParams(user, { accountUuid, unitUuid, amountCents: amount });
      const checkout = await createCheckout(params);

      // The provider returns the shopper on a bare path with no query string,
      // so stash our checkout id for the landing page before handing off.
      stashPendingCheckoutUuid(checkout.checkoutUuid);
      onCheckoutCreated(checkout);

      // Full-page navigation: the hosted checkout needs a top-level browsing
      // context for 3-D Secure. Never an iframe or XHR.
      window.location.assign(checkout.redirectUrl);
    } catch (error) {
      // On success the page navigates away; keeping loading=true prevents the
      // button flashing back to enabled during the redirect.
      setLoading(false);
      onError(error);
    }
  };

  return (
    <Button
      variant="cta"
      onClick={handlePaymentClick}
      disabled={loading || !amount || !unitUuid || !accountUuid || disabled}
      className="w-full"
    >
      {loading ? (
        <span className="flex items-center justify-center">
          <svg className="animate-spin -ml-1 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          Processing...
        </span>
      ) : (
        `Pay R${(amount / 100).toFixed(2)}`
      )}
    </Button>
  );
};

export default PaymentButton; 